import React, { useContext, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import config from 'app/src/config';
import ClientContext from 'app/src/contexts/ClientContext';
import { Loading } from 'app/src/elements/DataState';
import { useNavigate, useParams } from 'app/src/utils/routing';
import {
  Fields,
  defaultValues,
  PHONE_PATTERN,
  ZIP_CODE_PATTERN,
} from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Title } from 'app/src/styles';

import { CREATE_DEALER } from './queries';
import LicenseInput, { LICENSE_RULES } from './LicenseInput';
import RegisterCode from './RegisterCode';

export const FIELDS = {
  name: {
    labelTranslation: 'labels.dealershipName',
    hintTranslation: 'hints.dealer.namePrintedOnTags',
    rules: { required: true }
  },
  street: { rules: { required: true } },
  city: { rules: { required: true } },
  state: {
    rules: {
      required: true,
      maxLength: { value: 2, message: 'errors.stateFormat' }
    }
  },
  zip: {
    rules: {
      required: true,
      pattern: {
        value: ZIP_CODE_PATTERN,
        message: 'errors.zipFormat',
      },
    }
  },
  phone: {
    labelTranslation: 'labels.primaryPhone',
    rules: {
      pattern: {
        value: PHONE_PATTERN,
        message: 'errors.phoneNumberFormat',
      },
    },
  },
  url: {
    labelTranslation: 'labels.websiteUrl',
    hintTranslation: 'hints.optional'
  },
};

const NewDealer = ({ pending, onCreated }) => {
  const { currentUser, refetchCurrentUser } = useContext(ClientContext);
  const [formStatus, setFormStatus] = useState();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fields = { ...FIELDS };

  fields.street = {
    ...fields.street,
    type: 'addressStreet',
  };

  fields.note = {
    labelTranslation: 'labels.additionalNotes',
    hintTranslation: 'hints.dealer.additionalNotes',
  };

  const {
    control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: {
      ...defaultValues({ fields }),
      licensesAttributes: [
        { licenseType: '', licenseNumber: '' },
        { licenseType: '', licenseNumber: '' },
      ],
    }
  });

  const [createDealer] = useMutation(CREATE_DEALER, {
    onCompleted: data => {
      if (onCreated) {
        refetchCurrentUser();
        onCreated(data.createDealer.dealer);
      } else if (pending) {
        refetchCurrentUser();
      } else {
        refetchCurrentUser();
        navigate('/dealers');
      }
    }
  });

  const onSubmit = input => {
    setFormStatus('submitting');
    createDealer({ variables: input });
  };

  const licensesAttributes = watch('licensesAttributes');

  return (
    <View>
      {!pending && (
        <Title>{t('titles.addDealer')}</Title>
      )}

      {!currentUser?.isEmployee && <RegisterCode />}

      {licensesAttributes.map((license, index) => (
        <LicenseInput
          key={index}
          license={license}
          index={index}
          control={control}
          errors={errors}
          hint={0 === index ? 'hints.dealer.license' : 'hints.optional'}
          rules={0 === index ? LICENSE_RULES : {}}
          checkRegistered={!currentUser?.isEmployee}
        />
      ))}

      <Fields
        fields={fields}
        control={control}
        errors={errors}
        setValue={setValue}
      />

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button
          label={t('actions.save')}
          onPress={handleSubmit(onSubmit)}
          wide
        />
      )}
    </View>
  );
};

export default NewDealer;
